import { useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import {
  Container,
  Typography,
  Card,
  Stack,
  TextField,
  Box,
  Alert,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Button,
  Snackbar,
} from '@mui/material';
import { get, patch } from 'src/utils/networkUtils';

export default function OptaSquadsPage() {
  const [contestants, setContestants] = useState(null);
  const [contestantsError, setContestantsError] = useState(null);
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState(null); // contestant object
  const [persons, setPersons] = useState(null);
  const [personsError, setPersonsError] = useState(null);
  const [editCode, setEditCode] = useState('');
  const [saving, setSaving] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  useEffect(() => {
    const handle = setTimeout(() => {
      const qs = search.trim()
        ? `?search=${encodeURIComponent(search.trim())}`
        : "";
      get(`/admin/opta/contestants${qs}`)
        .then((r) => {
          const items = r?.data ?? [];
          setContestants(items);
          setContestantsError(null);
        })
        .catch((err) => {
          setContestants([]);
          setContestantsError(
            err?.response?.data?.message ?? err?.message ?? "Load failed"
          );
        });
    }, 200); // debounce
    return () => clearTimeout(handle);
  }, [search]);

  useEffect(() => {
    setEditCode(selected?.contestant_code ?? '');
  }, [selected]);

  useEffect(() => {
    if (!selected) {
      setPersons(null);
      setPersonsError(null);
      return;
    }
    setPersons(null);
    get(`/admin/opta/contestants/${selected.id}/persons`)
      .then((r) => {
        setPersons(r?.data ?? []);
        setPersonsError(null);
      })
      .catch((err) => {
        setPersons([]);
        setPersonsError(
          err?.response?.data?.message ?? err?.message ?? "Load failed"
        );
      });
  }, [selected]);

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleSaveCode = async () => {
    if (!selected) return;
    const value = editCode.trim() || null;
    setSaving(true);
    try {
      await patch(`/admin/opta/contestants/${selected.id}`, {
        contestant_code: value,
      });
      showSnackbar('Contestant code updated');
      setSelected(prev => (prev ? { ...prev, contestant_code: value } : prev));
      setContestants(prev =>
        prev ? prev.map(c => (c.id === selected.id ? { ...c, contestant_code: value } : c)) : prev
      );
    } catch (err) {
      showSnackbar(err?.response?.data?.message ?? err?.message ?? 'Update failed', 'error');
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <Helmet>
        <title>OPTA Squads | EngageCraft Fantasy Football Admin</title>
      </Helmet>
      <Container maxWidth="xl">
        <Typography variant="h4" sx={{ mb: 1 }}>
          OPTA Squads
          {contestants !== null && ` (${contestants.length})`}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Real teams + players imported from the OPTA <code>squads</code> feed.
          Pick a team on the left to see its current roster on the right. Use
          the OPTA Import page to refresh this data.
        </Typography>

        <TextField
          fullWidth
          size="small"
          placeholder="Search teams by name…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{ mb: 2 }}
        />

        <Stack
          direction={{ xs: "column", md: "row" }}
          spacing={2}
          alignItems="flex-start"
        >
          {/* Left: contestants */}
          <Card sx={{ flex: 1, minWidth: 0 }}>
            {contestantsError && (
              <Alert severity="error" sx={{ m: 2 }}>
                {contestantsError}
              </Alert>
            )}
            {contestants === null ? (
              <Box sx={{ p: 4, textAlign: "center" }}>
                <CircularProgress />
              </Box>
            ) : (
              <TableContainer sx={{ maxHeight: 600 }}>
                <Table stickyHeader size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Team</TableCell>
                      <TableCell>Code</TableCell>
                      <TableCell>Country</TableCell>
                      <TableCell align="right">Players</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {contestants.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={4} align="center" sx={{ py: 4 }}>
                          No OPTA-imported teams yet. Run an import on the OPTA
                          Import page.
                        </TableCell>
                      </TableRow>
                    ) : (
                      contestants.map((c) => (
                        <TableRow
                          key={c.id}
                          hover
                          selected={selected?.id === c.id}
                          onClick={() => setSelected(c)}
                          sx={{ cursor: "pointer" }}
                        >
                          <TableCell>
                            <Box
                              sx={{
                                fontWeight: selected?.id === c.id ? 600 : 400,
                              }}
                            >
                              {c.name}
                            </Box>
                            <Box sx={{ fontSize: 11, color: "text.secondary" }}>
                              {c.short_name}
                              {c.is_national_team ? " · national" : ""}
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Box sx={{ fontFamily: "monospace", fontSize: 11 }}>
                              {c.contestant_code ?? "-"}
                            </Box>
                          </TableCell>
                          <TableCell>{c.country ?? "-"}</TableCell>
                          <TableCell align="right">
                            <Chip label={c.player_count ?? 0} size="small" />
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Card>

          {/* Right: persons of selected contestant */}
          <Card sx={{ flex: 1.4, minWidth: 0 }}>
            <Box sx={{ p: 2, borderBottom: 1, borderColor: "divider" }}>
              <Typography variant="subtitle1">
                {selected ? selected.name : "Select a team to see its players"}
              </Typography>
              {selected && (
                <>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    OPTA id: <code>{selected.opta_contestant_id}</code>
                  </Typography>
                  <Stack direction="row" spacing={1} alignItems="flex-start">
                    <TextField
                      label="Flag / Code"
                      size="small"
                      value={editCode}
                      onChange={e => setEditCode(e.target.value)}
                      onKeyDown={e => {
                        if (e.key === 'Enter') handleSaveCode();
                      }}
                      disabled={saving}
                      sx={{ flex: 1 }}
                      helperText="Saved to contestant_flag_key"
                    />
                    <Button
                      variant="contained"
                      size="small"
                      onClick={handleSaveCode}
                      disabled={saving || editCode === (selected.contestant_code ?? '')}
                      sx={{ mt: 0.5 }}
                    >
                      {saving ? <CircularProgress size={20} /> : 'Save'}
                    </Button>
                  </Stack>
                </>
              )}
            </Box>
            {personsError && (
              <Alert severity="error" sx={{ m: 2 }}>
                {personsError}
              </Alert>
            )}
            {!selected ? (
              <Box sx={{ p: 4, color: "text.secondary", textAlign: "center" }}>
                Pick a team on the left.
              </Box>
            ) : persons === null ? (
              <Box sx={{ p: 4, textAlign: "center" }}>
                <CircularProgress />
              </Box>
            ) : persons.length === 0 ? (
              <Box sx={{ p: 4, color: "text.secondary", textAlign: "center" }}>
                No players in this team.
              </Box>
            ) : (
              <TableContainer sx={{ maxHeight: 600 }}>
                <Table stickyHeader size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Player</TableCell>
                      <TableCell>Position</TableCell>
                      <TableCell>Nationality</TableCell>
                      <TableCell>DoB</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {persons.map((p) => (
                      <TableRow key={p.id} hover>
                        <TableCell>
                          {p.first_name} {p.last_name}
                          <Box
                            sx={{
                              fontSize: 10,
                              color: "text.secondary",
                              fontFamily: "monospace",
                            }}
                          >
                            {p.opta_person_id}
                          </Box>
                        </TableCell>
                        <TableCell>{p.position ?? "-"}</TableCell>
                        <TableCell>{p.nationality ?? "-"}</TableCell>
                        <TableCell>{p.date_of_birth ?? "-"}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Card>
        </Stack>
      </Container>
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar(s => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar(s => ({ ...s, open: false }))}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
}
